import { useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import { Card, useToast } from '../../design-system';
// The `td-*` rules live with the task dashboard's stylesheet. Imported here
// rather than copied into a second file: the selectors are shared with the
// dashboard's other cards (.td-card-head, .td-pencil, .td-inline-input …), and
// splitting them would leave two definitions to keep in step. CSS is global and
// the import is deduped, so the admin page is unaffected and the employee page
// simply gains the rules it needs.
import '../styles/OrgAdminTaskDashboard.css';

// The task's checklist — the step list for the work itself.
//
// Extracted from OrgAdminTaskDashboard so the EMPLOYEE task page can show it
// too. It was admin-only purely because it lived inside that file: the routes
// (`POST/PUT/DELETE /api/tasks/:id/checklist`) carry no permission gate beyond
// "the task isn't completed", so the person actually doing the work was the one
// person who could not see the steps. Copying it would have been the drift the
// rest of this codebase avoids — hence one component, two callers.
//
// Self-sufficient by design: it reads the current user and the toaster itself,
// so a caller needs only the task and a way to write it back.

const AVATAR_COLORS = ['#4f6f5c', '#9c5b3e', '#b08428', '#2b3a4a', '#6e6b66', '#7a6a9c'];

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join('');
}

function Avatar({ name, size = 18 }) {
  const color = AVATAR_COLORS[(name?.length || 0) % AVATAR_COLORS.length];
  return (
    <span
      className="td-avatar"
      style={{ width: size, height: size, background: color, fontSize: size * 0.42 }}
      title={name}
    >
      {initials(name)}
    </span>
  );
}

// "Today at 11:19 AM" for something ticked off today, a dated stamp otherwise.
function fmtCompleted(value) {
  if (!value) return '';
  const d = new Date(value);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === new Date().toDateString()) return `Today at ${time}`;
  const date = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${date} at ${time}`;
}

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M14.5 5.5l4 4M4 20l1-4L16 5a1.5 1.5 0 0 1 2 0l1 1a1.5 1.5 0 0 1 0 2L8 19Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const KebabIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5.5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="18.5" r="1.5" /></svg>
);

function TaskChecklistCard({ task, setTask, taskId, readOnly = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [menuFor, setMenuFor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  const items = task.checklist || [];
  const done = items.filter((i) => i.isDone).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  // Ticking an item is the commonest action on this card by a wide margin, so
  // it paints immediately and rolls back if the server disagrees.
  const toggle = async (item) => {
    if (readOnly) return;
    const nextDone = !item.isDone;
    const prev = task;
    setTask({
      ...task,
      checklist: items.map((c) =>
        c._id === item._id
          ? {
              ...c,
              isDone: nextDone,
              completedBy: nextDone ? { name: user?.name } : null,
              completedAt: nextDone ? new Date().toISOString() : null,
            }
          : c
      ),
    });
    try {
      const res = await apiClient.put(`/api/tasks/${taskId}/checklist/${item._id}`, { isDone: nextDone });
      setTask(res.data);
    } catch {
      setTask(prev);
      toast.danger('Failed to update item');
    }
  };

  const addItem = async () => {
    const label = draft.trim();
    if (!label) { setAdding(false); return; }
    setDraft('');
    setAdding(false);
    try {
      const res = await apiClient.post(`/api/tasks/${taskId}/checklist`, { label });
      setTask(res.data);
    } catch {
      toast.danger('Failed to add item');
    }
  };

  const saveEdit = async (item) => {
    const label = editDraft.trim();
    setEditingId(null);
    if (!label || label === item.label) return;
    try {
      const res = await apiClient.put(`/api/tasks/${taskId}/checklist/${item._id}`, { label });
      setTask(res.data);
    } catch {
      toast.danger('Failed to rename item');
    }
  };

  const remove = async (item) => {
    setMenuFor(null);
    try {
      const res = await apiClient.delete(`/api/tasks/${taskId}/checklist/${item._id}`);
      setTask(res.data);
    } catch {
      toast.danger('Failed to delete item');
    }
  };

  return (
    <Card padding="md" className="td-checklist-card">
      <div className="td-card-head">
        <span className="td-card-title">Checklist <span className="td-count">{done}/{items.length}</span></span>
        {!readOnly && (
          <button className="td-pencil" onClick={() => setAdding(true)} aria-label="Add checklist item"><PencilIcon /></button>
        )}
      </div>

      <div className="td-progress">
        <div className="td-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="td-checklist">
        {items.map((item) => (
          <div key={item._id} className={`td-check-row ${item.isDone ? 'is-done' : ''}`}>
            <button
              className={`td-checkbox ${item.isDone ? 'checked' : ''}`}
              onClick={() => toggle(item)}
              disabled={readOnly}
              aria-label="Toggle item"
            >
              {item.isDone && <CheckIcon />}
            </button>
            <div className="td-check-body">
              {editingId === item._id ? (
                <input
                  className="td-inline-input"
                  autoFocus
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onBlur={() => saveEdit(item)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(item); if (e.key === 'Escape') setEditingId(null); }}
                />
              ) : (
                <span className="td-check-text">{item.label}</span>
              )}
              {item.isDone && item.completedAt && (
                <span className="td-check-meta">
                  <Avatar name={item.completedBy?.name} size={18} />
                  {item.completedBy?.name || 'Someone'} · {fmtCompleted(item.completedAt)}
                </span>
              )}
            </div>
            {!readOnly && (
              <div className="td-kebab-wrap">
                <button className="td-icon-btn" onClick={() => setMenuFor(menuFor === item._id ? null : item._id)} aria-label="Item options"><KebabIcon /></button>
                {menuFor === item._id && (
                  <div className="td-kebab-menu">
                    <button onClick={() => { setEditingId(item._id); setEditDraft(item.label); setMenuFor(null); }}>Edit</button>
                    <button className="td-danger" onClick={() => remove(item)}>Delete</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {adding && (
          <div className="td-check-row">
            <span className="td-checkbox" />
            <input
              className="td-inline-input"
              autoFocus
              placeholder="New checklist item"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={addItem}
              onKeyDown={(e) => { if (e.key === 'Enter') addItem(); if (e.key === 'Escape') { setDraft(''); setAdding(false); } }}
            />
          </div>
        )}

        {items.length === 0 && !adding && <div className="td-muted">No checklist items yet</div>}
      </div>
    </Card>
  );
}

export default TaskChecklistCard;
